import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useModal } from '../../hooks/useModal';
import { useAuth } from '../../hooks/useAuth';
import { overlayStyles, cn } from '../../utils/classNames';
import { HelpSection, HelpSearchResult, HelpFAQ, HelpGuide } from '../../types';
import {
  helpCategories,
  getHelpSectionsByRole,
  getHelpFAQsByRole,
  getHelpGuidesByRole,
} from '../../data/helpContent';
import HelpFAQComponent from './HelpFAQ';
import HelpGuideComponent from './HelpGuide';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  pageContext?: string;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose, pageContext }) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<HelpSection | null>(null);
  const [viewMode, setViewMode] = useState<'sections' | 'faq' | 'guides'>('sections');
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // モーダル管理
  const { handleOverlayClick } = useModal({
    isOpen,
    onClose,
    containerRef: modalRef,
    closeOnEscape: true,
    closeOnOverlayClick: true,
  });

  // 検索入力をリセット
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectedCategory(null);
      setSelectedSection(null);
      setViewMode('sections');
    }
  }, [isOpen]);

  // セクション選択時にスクロール位置をトップに戻す
  useEffect(() => {
    if (selectedSection && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [selectedSection]);

  // データはhelpContentから取得

  // 役割とページコンテキストに基づいてデータをフィルタリング
  const getFilteredSections = (): HelpSection[] => {
    if (!user) return [];
    return getHelpSectionsByRole(user.role, pageContext);
  };

  const getFilteredFAQs = (): HelpFAQ[] => {
    if (!user) return [];
    return getHelpFAQsByRole(user.role, pageContext);
  };

  const getFilteredGuides = (): HelpGuide[] => {
    if (!user) return [];
    return getHelpGuidesByRole(user.role);
  };

  // 検索機能
  const searchSections = (query: string): HelpSearchResult[] => {
    if (!query.trim()) return [];

    const filtered = getFilteredSections();
    const results: HelpSearchResult[] = [];

    filtered.forEach(section => {
      let score = 0;
      const searchTerms = query.toLowerCase().split(' ');

      searchTerms.forEach(term => {
        // タイトルでの一致（高スコア）
        if (section.title.toLowerCase().includes(term)) {
          score += 0.6;
        }

        // コンテンツでの一致（中スコア）
        if (section.content.toLowerCase().includes(term)) {
          score += 0.3;
        }

        // キーワードでの一致（中スコア）
        if (section.searchKeywords.some(keyword => keyword.toLowerCase().includes(term))) {
          score += 0.4;
        }
      });

      if (score > 0) {
        results.push({ section, matchScore: Math.min(score, 1) });
      }
    });

    return results.sort((a, b) => b.matchScore - a.matchScore);
  };

  const filteredSections = getFilteredSections();
  const filteredFAQs = getFilteredFAQs();
  const filteredGuides = getFilteredGuides();

  const searchResults = searchQuery ? searchSections(searchQuery) : [];

  // 検索時のFAQ・ガイドフィルタリング
  const searchedFAQs = searchQuery
    ? filteredFAQs.filter(
        faq =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : filteredFAQs;

  const searchedGuides = searchQuery
    ? filteredGuides.filter(
        guide =>
          guide.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          guide.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          guide.sections.some(
            section =>
              section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
              section.content.toLowerCase().includes(searchQuery.toLowerCase())
          )
      )
    : filteredGuides;

  // カテゴリでフィルタリングされたセクション
  const categorizedSections = selectedCategory
    ? filteredSections.filter(section => section.category.id === selectedCategory)
    : filteredSections;

  if (!isOpen) return null;

  return (
    <>
      {/* オーバーレイ */}
      <div
        className={cn(overlayStyles(), 'z-50')}
        onClick={handleOverlayClick}
        aria-hidden='true'
      />

      {/* モーダル */}
      <div
        ref={modalRef}
        className='fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-[80vh] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden flex flex-col'
        role='dialog'
        aria-labelledby='help-modal-title'
        aria-modal='true'
      >
        {/* ヘッダー */}
        <div className='flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0'>
          <h2 id='help-modal-title' className='text-xl font-semibold text-gray-800 dark:text-white'>
            ヘルプ
            {pageContext && (
              <span className='text-sm font-normal text-gray-500 dark:text-gray-400 ml-2'>
                - {pageContext}
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className='p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition'
            aria-label='ヘルプを閉じる'
          >
            <XMarkIcon className='h-6 w-6' />
          </button>
        </div>

        <div className='flex flex-1 overflow-hidden'>
          {/* サイドバー */}
          <div className='w-1/3 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto'>
            {/* 検索ボックス */}
            <div className='relative mb-4'>
              <MagnifyingGlassIcon className='absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400' />
              <input
                type='text'
                placeholder='ヘルプを検索...'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className='w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white'
              />
            </div>

            {/* ビューモードタブ */}
            <div className='flex border-b border-gray-200 dark:border-gray-700 mb-4'>
              {[
                { key: 'sections', label: 'ヘルプ' },
                { key: 'faq', label: 'FAQ' },
                { key: 'guides', label: 'ガイド' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => {
                    setViewMode(key as 'sections' | 'faq' | 'guides');
                    setSelectedSection(null);
                    setSelectedCategory(null);
                  }}
                  className={cn(
                    'flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors',
                    viewMode === key
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* 検索結果 */}
            {searchQuery && searchResults.length > 0 && (
              <div className='mb-4'>
                <h3 className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                  検索結果 ({searchResults.length}件)
                </h3>
                <div className='space-y-1'>
                  {searchResults.map(({ section }) => (
                    <button
                      key={section.id}
                      onClick={() =>
                        setSelectedSection(prev => (prev?.id === section.id ? null : section))
                      }
                      className='w-full text-left p-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition'
                    >
                      {section.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* カテゴリリスト */}
            {!searchQuery && viewMode === 'sections' && (
              <>
                <h3 className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                  カテゴリ
                </h3>
                <div className='space-y-1'>
                  <button
                    onClick={() => {
                      setSelectedCategory(null);
                      setSelectedSection(null);
                    }}
                    className={cn(
                      'w-full text-left p-2 text-sm rounded transition flex items-center justify-between',
                      selectedCategory === null
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    )}
                  >
                    すべて
                    <ChevronRightIcon className='h-4 w-4' />
                  </button>
                  {helpCategories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => {
                        setSelectedCategory(category.id);
                        setSelectedSection(null);
                      }}
                      className={cn(
                        'w-full text-left p-2 text-sm rounded transition flex items-center justify-between',
                        selectedCategory === category.id
                          ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      )}
                    >
                      {category.name}
                      <ChevronRightIcon className='h-4 w-4' />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* メインコンテンツ */}
          <div ref={contentRef} className='flex-1 p-6 overflow-y-auto'>
            {selectedSection ? (
              // 選択されたセクションの詳細表示
              <div>
                <h3 className='text-lg font-semibold text-gray-800 dark:text-white mb-4'>
                  {selectedSection.title}
                </h3>
                <div className='prose dark:prose-invert max-w-none'>
                  <p className='text-gray-600 dark:text-gray-300'>{selectedSection.content}</p>
                </div>
              </div>
            ) : searchQuery &&
              viewMode === 'sections' &&
              searchResults.length === 0 &&
              searchedFAQs.length === 0 &&
              searchedGuides.length === 0 ? (
              // 検索結果なし
              <div className='text-center py-8'>
                <MagnifyingGlassIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                <p className='text-gray-500 dark:text-gray-400'>
                  「{searchQuery}」に関するヘルプが見つかりませんでした。
                </p>
              </div>
            ) : (
              // ビューモード別コンテンツ表示
              <div>
                {viewMode === 'sections' && (
                  <>
                    <h3 className='text-lg font-semibold text-gray-800 dark:text-white mb-4'>
                      {selectedCategory
                        ? helpCategories.find(c => c.id === selectedCategory)?.name
                        : 'すべてのヘルプ'}
                    </h3>
                    <div className='space-y-4'>
                      {categorizedSections.map(section => (
                        <button
                          key={section.id}
                          onClick={() =>
                            setSelectedSection(prev => (prev?.id === section.id ? null : section))
                          }
                          className='w-full text-left p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition'
                        >
                          <h4 className='font-medium text-gray-800 dark:text-white mb-2'>
                            {section.title}
                          </h4>
                          <p className='text-sm text-gray-600 dark:text-gray-300 line-clamp-2'>
                            {section.content}
                          </p>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {viewMode === 'faq' && (
                  <HelpFAQComponent faqs={searchedFAQs} searchQuery={searchQuery} />
                )}

                {viewMode === 'guides' && (
                  <HelpGuideComponent guides={searchedGuides} searchQuery={searchQuery} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default HelpModal;
